import type { HostOs } from '@hibro/shared';

/**
 * `navigator.userAgentData` is not in the DOM lib yet; only the one field we read
 * is declared here (Chromium exposes it; Firefox/Safari do not).
 */
interface UADataNavigator extends Navigator {
  userAgentData?: { platform?: string };
}

/**
 * The OS this browser runs on, in the same three families the desktop reports
 * (见 18 / 19).
 *
 * Lives in the worker project so both the SharedWorker host (`context.os`) and
 * the page (stamp `hibroHost.os`) call **one** function. The page already
 * imports the worker control-plane (见 `tsconfig.web.json`).
 *
 * This *is* UA-derived, and deliberately so. The rule against environment
 * sniffing targets code asking "which runtime am I in" — a question the platform
 * already answers structurally (`ModuleRuntime`, injected bridges, capability
 * profiles), so sniffing it means the judgement and the intent have come apart.
 * "Which OS is the user on" is a different question, it has no other source in a
 * browser, and it is asked **here in the shell** — resolved once and handed to
 * modules through `context.os` / `window.hibroHost`, so no module ever touches
 * the UA itself.
 *
 * `userAgentData.platform` first (a real field rather than a parsed string), then
 * the legacy `navigator.platform`. Anything unrecognized lands on `'linux'`,
 * which is what the non-mac / non-Windows UI already assumes.
 */
export function resolveBrowserOs(): HostOs {
  const hinted = (navigator as UADataNavigator).userAgentData?.platform;
  const raw = hinted && hinted.trim() !== '' ? hinted : navigator.platform;
  const value = raw.toLowerCase();
  if (value.includes('mac')) {
    return 'darwin';
  }
  if (value.includes('win')) {
    return 'win32';
  }
  return 'linux';
}
